/**
 * «Lo que le sobra primero» (20/10/2026).
 *
 * Norman, con las vacaciones de Irving del 21/12/2026 al 08/01/2027, que
 * empiezan tres días después de su aniversario: «esas vacaciones son de este
 * periodo». Eligió que lo que se pide en un periodo salga primero de lo que
 * le sobró a los anteriores y después de sus días de goce. Lo gozado fuera
 * de COS (la hoja de RH) se queda contra los días de su periodo, como lo
 * tiene RH.
 *
 * Con qué se valida no cambia: diasTomados, restantes, diasArrastre y
 * disponibles siguen siendo la cuenta de siempre (el periodo en que empiezan
 * las fechas, con lo que le sobra al anterior ya sumado). Lo que cambia es lo
 * que se ENSEÑA: de cuál periodo salen los días. El servidor lo manda hecho
 * (VacacionBuss.primeroLoQueSobra: gozadosVista, restantesVista,
 * acumuladoVista, quedanVista, deLoAcumulado y prestados) y aquí se lee igual
 * en «Mis vacaciones», la Configuración del Calendario y el Seguimiento
 * (Mi equipo y RH). La misma cuenta, en primeroLoQueSobra, sirve para decir
 * en «Revisa» de dónde saldrían los días que se están pidiendo.
 */
import { CuentaDePeriodo, Tramo } from "./solicitud-vacaciones/tramos-vacaciones";

/** Lo que hace falta de un periodo para la cuenta: los campos del Saldo. */
export interface PeriodoDeSaldo {
  diasTotales?: number | null;
  diasTomados?: number | null;
  diasGozadosPrevios?: number | null;
  diasArrastre?: number | null;
  arrastreCalculado?: boolean | null;
  restantes?: number | null;
  disponibles?: number | null;
  gozadosVista?: number | null;
  restantesVista?: number | null;
  acumuladoVista?: number | null;
  quedanVista?: number | null;
  deLoAcumulado?: number | null;
  prestados?: number | null;
}

/* ---------- Lo que se enseña de un periodo ----------
 * Si el servidor no lo mandara (uno viejo), se enseña la cuenta de siempre. */

/** El servidor ya mandó la cuenta «lo que le sobra primero». */
function conVista(s: PeriodoDeSaldo): boolean {
  return s.gozadosVista != null;
}

/** «Gozados»: lo que sale de los días de goce de este periodo. */
export function gozadosVista(s: PeriodoDeSaldo): number {
  return conVista(s) ? s.gozadosVista || 0 : (s.diasTomados || 0) + (s.diasGozadosPrevios || 0);
}

/** «Restantes»: días de goce menos gozados. null sin días asignados. */
export function restantesVista(s: PeriodoDeSaldo): number | null {
  if (s.diasTotales == null) {
    return null;
  }
  if (conVista(s) && s.restantesVista != null) {
    return s.restantesVista;
  }
  return s.restantes ?? s.diasTotales - gozadosVista(s);
}

/** «Acumulado»: lo que sobró de periodos anteriores y no se ha usado. null si no se sabe. */
export function acumuladoVista(s: PeriodoDeSaldo): number | null {
  return conVista(s) ? s.acumuladoVista ?? null : s.diasArrastre ?? null;
}

/** «Le quedan»: restantes más acumulado. null sin días asignados. */
export function quedanVista(s: PeriodoDeSaldo): number | null {
  const restantes = restantesVista(s);
  if (restantes === null) {
    return null;
  }
  if (conVista(s) && s.quedanVista != null) {
    return s.quedanVista;
  }
  return s.disponibles ?? restantes + (s.diasArrastre || 0);
}

/** De gozados, lo que es de COS (lo demás es lo que trae la hoja de RH). */
export function gozadosEnCos(s: PeriodoDeSaldo): number {
  return gozadosVista(s) - (s.diasGozadosPrevios || 0);
}

/** De lo que se pidió para periodos posteriores, lo que sale de los días de este. */
export function paraElSiguiente(s: PeriodoDeSaldo): number {
  if (!conVista(s)) {
    return 0;
  }
  return gozadosVista(s) - ((s.diasTomados || 0) - (s.deLoAcumulado || 0)) - (s.diasGozadosPrevios || 0);
}

/** De lo acumulado, lo que ya se usó (en este periodo o en los que siguen). */
export function acumuladoUsado(s: PeriodoDeSaldo): number {
  const queda = acumuladoVista(s);
  return queda === null ? 0 : Math.max(0, s.diasArrastre || 0) - Math.max(0, queda);
}

/* ---------- La cuenta, para lo que se está pidiendo ---------- */

/** Lo que se enseña de un periodo después de la cuenta. */
export interface VistaDePeriodo {
  gozados: number;
  restantes: number | null;
  acumulado: number;
  quedan: number | null;
  deLoAcumulado: number;
  prestados: number;
}

/**
 * La misma cuenta que VacacionBuss.completa más primeroLoQueSobra, sobre los
 * periodos que tiene la pantalla, en orden: el vigente y los que siguen. El
 * acumulado del primero se toma como llega (viene de periodos que aquí no
 * están); el de los demás, si es calculado, es lo que le queda al anterior.
 * `extra` suma días pedidos a cada periodo (por posición): así se ve cómo
 * quedaría con lo que se está eligiendo.
 */
export function primeroLoQueSobra(periodos: PeriodoDeSaldo[], extra: number[] = []): VistaDePeriodo[] {
  const tomados = periodos.map((p, i) => (p.diasTomados || 0) + (extra[i] || 0));
  const acumulados: number[] = [];
  let anterior: number | null = null;
  periodos.forEach((p, i) => {
    const acumulado = i > 0 && p.arrastreCalculado !== false ? anterior ?? 0 : p.diasArrastre || 0;
    acumulados.push(acumulado);
    anterior = p.diasTotales == null ? null : p.diasTotales - tomados[i] - (p.diasGozadosPrevios || 0) + acumulado;
  });

  const r: VistaDePeriodo[] = [];
  let prestados = 0;
  for (let i = periodos.length - 1; i >= 0; i--) {
    const p = periodos[i];
    const previos = p.diasGozadosPrevios || 0;
    const acumulado = acumulados[i];
    if (p.diasTotales == null) {
      r[i] = { gozados: tomados[i] + previos, restantes: null, acumulado, quedan: null, deLoAcumulado: 0, prestados };
      prestados = 0;
      continue;
    }
    const sobra = Math.max(0, acumulado);
    const deLoAcumulado = Math.min(tomados[i], sobra);
    const prestadosDeLoAcumulado = Math.min(prestados, sobra - deLoAcumulado);
    const gozados = tomados[i] - deLoAcumulado + previos + prestados - prestadosDeLoAcumulado;
    const restantes = p.diasTotales - gozados;
    const queda = acumulado - deLoAcumulado - prestadosDeLoAcumulado;
    r[i] = { gozados, restantes, acumulado: queda, quedan: restantes + queda, deLoAcumulado, prestados };
    prestados = p.arrastreCalculado === false ? 0 : deLoAcumulado + prestadosDeLoAcumulado;
  }
  return r;
}

/**
 * De qué periodo salen los días que se están pidiendo y cuánto le quedaría
 * a cada uno, para «Revisa». Del primero de la lista (el vigente) se cuenta
 * lo que le queda con su acumulado; de los que siguen, sólo sus días de goce,
 * porque lo que les llega acumulado es lo que le sobra al anterior y ya se
 * cuenta ahí. A Irving (le quedan 15; pide 13 desde el 21/12/2026, ya en su
 * periodo siguiente) le sale «del 18/12/2025 al 18/12/2026: te quedarían 2
 * de 15». Un periodo sin días asignados al que caen fechas sale sin cuenta.
 */
export function fuentesDelPedido<P extends PeriodoDeSaldo>(
  tramos: Tramo[],
  periodos: P[],
  periodoDe: (dia: Date) => P | null,
): CuentaDePeriodo<P>[] {
  const extra = periodos.map(() => 0);
  for (const t of tramos) {
    const p = periodoDe(t.desde);
    const i = p ? periodos.indexOf(p) : -1;
    if (i >= 0) {
      extra[i] += t.dias;
    }
  }
  const antes = primeroLoQueSobra(periodos);
  const despues = primeroLoQueSobra(periodos, extra);
  const r: CuentaDePeriodo<P>[] = [];
  periodos.forEach((periodo, i) => {
    const soloLoSuyo = i > 0 && periodo.arrastreCalculado !== false;
    const tiene = soloLoSuyo ? antes[i].restantes : antes[i].quedan;
    const quedarian = soloLoSuyo ? despues[i].restantes : despues[i].quedan;
    if (tiene === null || quedarian === null) {
      if (extra[i] > 0) {
        r.push({ periodo, pide: extra[i], tiene: null, quedarian: null });
      }
    } else if (quedarian < tiene) {
      r.push({ periodo, pide: tiene - quedarian, tiene, quedarian });
    }
  });
  return r;
}
